/**
 * 客户端遥测：把浏览器侧的报错 / 异常静默回传到 `/api/v1/client-logs`。
 *
 * 设计约束：
 *   - 永远不抛异常。上报本身失败时吞掉，不能因为"上报失败"导致原本
 *     正常的代码路径炸出第二个错误（这正是生产事故里最常见的反模式）。
 *   - 不阻塞业务路径。reportClientError 同步把记录塞进内存队列，
 *     真正的网络请求在下一个 tick 由队列 flush 出去。
 *   - 客户端自带节流：1 秒内多条合并为单次 batch，每分钟上限 N 条；
 *     防止浏览器 crash loop 时把后端打爆。
 *   - 不依赖 ApiClient：ApiClient 自己在 fetch 失败时会再调用
 *     reportClientError，避免循环依赖；这里走原生 fetch + AbortController。
 */

import { apiPaths } from '../api/paths';

export type ClientLogSeverity = 'info' | 'warn' | 'error' | 'fatal';

export type ClientLogSource =
  | 'window.onerror'
  | 'unhandledrejection'
  | 'fetch'
  | 'react-boundary'
  | 'manual';

export interface ClientLogRecord {
  severity: ClientLogSeverity;
  source: ClientLogSource;
  message: string;
  stack?: string;
  occurred_at: number;
  context?: Record<string, unknown>;
}

interface ClientLogBatch {
  records: ClientLogRecord[];
}

const MAX_BATCH_SIZE = 20;
const FLUSH_INTERVAL_MS = 1000;
const MAX_PER_MINUTE = 60;

let queue: ClientLogRecord[] = [];
let flushing = false;
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let recentCount = 0;
let recentWindowStart = Date.now();

function nowMs(): number {
  return Date.now();
}

function trimStack(s: string, max = 16 * 1024): string {
  if (s.length <= max) return s;
  return s.slice(0, max);
}

function trimMessage(s: string, max = 2 * 1024): string {
  if (s.length <= max) return s;
  return s.slice(0, max);
}

function takeRateLimit(): boolean {
  const now = nowMs();
  if (now - recentWindowStart >= 60_000) {
    recentWindowStart = now;
    recentCount = 0;
  }
  if (recentCount >= MAX_PER_MINUTE) {
    return false;
  }
  recentCount += 1;
  return true;
}

function scheduleFlush(): void {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flush();
  }, FLUSH_INTERVAL_MS);
}

async function flush(): Promise<void> {
  if (flushing) return;
  if (queue.length === 0) return;
  flushing = true;

  const batch: ClientLogRecord[] = queue.splice(0, MAX_BATCH_SIZE);
  const body: ClientLogBatch = { records: batch };

  try {
    const url = apiPaths.clientLogs.base;
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 5000);
    let res: Response;
    try {
      res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        credentials: 'include',
        signal: ctrl.signal,
        keepalive: true,
      });
    } finally {
      clearTimeout(t);
    }
    if (!res.ok && res.status !== 204) {
      /* swallow — telemetry must never bubble */
    }
  } catch {
    /* swallow — telemetry must never bubble */
  } finally {
    flushing = false;
    if (queue.length > 0) scheduleFlush();
  }
}

function errorToRecord(err: unknown, source: ClientLogSource, context?: Record<string, unknown>): ClientLogRecord {
  let message = '';
  let stack: string | undefined;
  if (err instanceof Error) {
    message = trimMessage(`${err.name}: ${err.message}`);
    if (err.stack) stack = trimStack(err.stack);
  } else if (typeof err === 'string') {
    message = trimMessage(err);
  } else {
    try {
      message = trimMessage(JSON.stringify(err));
    } catch {
      message = 'unknown error';
    }
  }
  return {
    severity: 'error',
    source,
    message,
    stack,
    occurred_at: nowMs(),
    context,
  };
}

/**
 * 上报一条客户端错误。同步入队、异步 flush；不抛异常、不阻塞调用方。
 */
export function reportClientError(
  err: unknown,
  source: ClientLogSource = 'manual',
  context?: Record<string, unknown>,
): void {
  if (typeof window === 'undefined') return;
  try {
    if (!takeRateLimit()) return;
    const rec = errorToRecord(err, source, context);
    queue.push(rec);
    if (queue.length >= MAX_BATCH_SIZE) {
      void flush();
    } else {
      scheduleFlush();
    }
  } catch {
    /* swallow */
  }
}

/**
 * 上报一条非错误的提示（warn / info）。同样静默、节流。
 */
export function reportClientEvent(
  message: string,
  severity: 'info' | 'warn' = 'info',
  source: ClientLogSource = 'manual',
  context?: Record<string, unknown>,
): void {
  if (typeof window === 'undefined') return;
  try {
    if (!takeRateLimit()) return;
    queue.push({
      severity,
      source,
      message: trimMessage(message),
      occurred_at: nowMs(),
      context,
    });
    scheduleFlush();
  } catch {
    /* swallow */
  }
}

/**
 * 把 window.onerror / unhandledrejection 装上。返回卸载函数。
 * 设计为幂等：多次调用只有第一次生效。
 */
let globalInstalled = false;
export function installGlobalErrorHandlers(): () => void {
  if (globalInstalled || typeof window === 'undefined') {
    return () => undefined;
  }
  globalInstalled = true;

  const onError = (event: ErrorEvent): void => {
    const ctx: Record<string, unknown> = {
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    };
    const err = event.error ?? `${event.message} @ ${event.filename}:${event.lineno}:${event.colno}`;
    reportClientError(err, 'window.onerror', ctx);
  };

  const onRejection = (event: PromiseRejectionEvent): void => {
    const reason = event.reason;
    const ctx: Record<string, unknown> = { kind: 'unhandledrejection' };
    if (reason && typeof reason === 'object') {
      const r = reason as { name?: string; code?: string; status?: number };
      if (r.code) ctx.code = r.code;
      if (r.status) ctx.status = r.status;
      if (r.name) ctx.name = r.name;
    }
    reportClientError(reason, 'unhandledrejection', ctx);
  };

  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onRejection);

  return () => {
    window.removeEventListener('error', onError);
    window.removeEventListener('unhandledrejection', onRejection);
    globalInstalled = false;
  };
}
